import type { CalculatorFormData } from '../calculatorTypes';
import type { CalcTexts } from '../locales';
import { kids } from '../calculatorUtils';
import styles from '../Calculator.module.css';

interface Props {
  formData: CalculatorFormData;
  onConfirm: () => void;
  onBack: (data: Partial<CalculatorFormData>) => void;
  onClose: () => void;
  isLoading: boolean;
  error?: string;
  texts: CalcTexts;
}

function toNum(v: string): number {
  const n = parseInt(v, 10);
  return isNaN(n) ? 0 : n;
}

function euro(v: string | number): string {
  const n = typeof v === 'string' ? toNum(v) : v;
  return n.toLocaleString('de-DE') + ' €';
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <p className={styles.summaryItem}>
      <span>– {label}: </span>
      <span>{value}</span>
    </p>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={styles.summarySection}>
      <p className={styles.summarySectionTitle}>{title}</p>
      {children}
    </div>
  );
}

export function SummaryStep({ formData, onConfirm, onBack, onClose, isLoading, error, texts }: Props) {
  const {
    shared_household,
    pregnant,
    children_0_5, children_6_13, children_14_17, children_18_24_non_working,
    child_1_age, child_2_age,
    checkbox_income_user, income_user_gross, income_user_net,
    checkbox_income_partner, income_partner_gross, income_partner_net,
    checkbox_other_income, rental_income, unemployment_benefit, pension, other_income_amount,
    checkbox_housing_costs, cold_rent, additional_costs, heating_costs,
  } = formData;

  const childrenTotal =
    children_0_5 + children_6_13 + children_14_17 + children_18_24_non_working;
  const hasChildren = childrenTotal > 0;

  const hasAnyOtherIncome =
    !checkbox_other_income &&
    (toNum(rental_income) > 0 ||
      toNum(unemployment_benefit) > 0 ||
      toNum(pension) > 0 ||
      toNum(other_income_amount) > 0);

  const hasAnyHousing =
    !checkbox_housing_costs &&
    (toNum(cold_rent) > 0 || toNum(additional_costs) > 0 || toNum(heating_costs) > 0);

  return (
    <div className={styles.stepContent}>
      <p className={styles.stepQuestion}>{texts.summary.intro}</p>

      <div className={styles.summaryBody}>
        {shared_household && (
          <Section title={texts.summary.sections.partnership}>
            <p className={styles.summaryItem}>– {texts.summary.values.lives_with_partner}</p>
          </Section>
        )}

        {hasChildren && (
          <Section title={texts.summary.sections.children}>
            {children_0_5 > 0 && (
              <p className={styles.summaryItem}>
                – {kids(children_0_5, texts)} ({texts.common.age_groups.age_0_5})
              </p>
            )}
            {children_6_13 > 0 && (
              <p className={styles.summaryItem}>
                – {kids(children_6_13, texts)} ({texts.common.age_groups.age_6_13})
              </p>
            )}
            {children_14_17 > 0 && (
              <p className={styles.summaryItem}>
                – {kids(children_14_17, texts)} ({texts.common.age_groups.age_14_17})
              </p>
            )}
            {children_18_24_non_working > 0 && (
              <p className={styles.summaryItem}>
                – {kids(children_18_24_non_working, texts)} ({texts.common.age_groups.age_18_24_non_working})
              </p>
            )}
            {child_1_age != null && (
              <p className={styles.summaryItem}>– {texts.child_ages.child_1_age_summary.replace('{age}', String(child_1_age))}</p>
            )}
            {child_2_age != null && (
              <p className={styles.summaryItem}>– {texts.child_ages.child_2_age_summary.replace('{age}', String(child_2_age))}</p>
            )}
            <p className={styles.summaryItem}>– {texts.summary.values.child_benefit_auto}</p>
          </Section>
        )}

        {hasAnyHousing && (
          <Section title={texts.summary.sections.housing_monthly}>
            {toNum(cold_rent) > 0 && <Row label={texts.housing.cold_rent} value={euro(cold_rent)} />}
            {toNum(additional_costs) > 0 && <Row label={texts.housing.additional_costs} value={euro(additional_costs)} />}
            {toNum(heating_costs) > 0 && <Row label={texts.housing.heating_costs} value={euro(heating_costs)} />}
          </Section>
        )}

        {pregnant && (
          <Section title={texts.summary.sections.special_circumstances}>
            <p className={styles.summaryItem}>
              – {shared_household
                ? texts.summary.values.pregnant_or_partner_pregnant_from_week_13
                : texts.summary.values.pregnant_from_week_13}
            </p>
          </Section>
        )}

        {(!checkbox_income_user && (toNum(income_user_gross) > 0 || toNum(income_user_net) > 0)) ||
         (shared_household && !checkbox_income_partner && (toNum(income_partner_gross) > 0 || toNum(income_partner_net) > 0)) ? (
          <Section title={texts.summary.sections.employment_income_monthly}>
            {!checkbox_income_user && toNum(income_user_gross) > 0 && (
              <Row label={texts.summary.labels.user_gross_income} value={euro(income_user_gross)} />
            )}
            {!checkbox_income_user && toNum(income_user_net) > 0 && (
              <Row label={texts.summary.labels.user_net_income} value={euro(income_user_net)} />
            )}
            {shared_household && !checkbox_income_partner && toNum(income_partner_gross) > 0 && (
              <Row label={texts.summary.labels.partner_gross_income} value={euro(income_partner_gross)} />
            )}
            {shared_household && !checkbox_income_partner && toNum(income_partner_net) > 0 && (
              <Row label={texts.summary.labels.partner_net_income} value={euro(income_partner_net)} />
            )}
          </Section>
        ) : null}

        {hasAnyOtherIncome && (
          <Section title={texts.summary.sections.other_income_monthly}>
            {toNum(rental_income) > 0 && <Row label={texts.other_income.rental_income} value={euro(rental_income)} />}
            {toNum(unemployment_benefit) > 0 && <Row label={texts.other_income.unemployment_benefit_i} value={euro(unemployment_benefit)} />}
            {toNum(pension) > 0 && <Row label={texts.other_income.pension} value={euro(pension)} />}
            {toNum(other_income_amount) > 0 && <Row label={texts.other_income.other} value={euro(other_income_amount)} />}
          </Section>
        )}
      </div>

      <p className={styles.summaryQuestion}>{texts.summary.question_is_correct}</p>

      {error && (
        <p role="alert" style={{ color: '#c0392b', fontSize: '0.875rem', marginBottom: '8px' }}>
          {error}
        </p>
      )}

      <div className={styles.summaryActions}>
        <button
          type="button"
          className={styles.btnBack}
          onClick={() => onBack({})}
          disabled={isLoading}
        >
          {'< '}{texts.common.buttons.back}
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={onConfirm}
          disabled={isLoading}
          style={{ position: 'relative' }}
        >
          <span style={{ visibility: isLoading ? 'hidden' : undefined }}>
            {texts.summary.actions.start_calculation}
          </span>
          {isLoading && (
            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {'...'}
            </span>
          )}
        </button>
        <button
          type="button"
          className={styles.btnOutline}
          id={styles.btnCancelSummary}
          onClick={onClose}
          disabled={isLoading}
        >
          {texts.common.buttons.cancel}
        </button>
      </div>
    </div>
  );
}
